import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useCascadeDelete from '../hooks/useCascadeDelete';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Rooms = () => {
  const { showSuccess, showError } = useNotificationContext();
  const { analyzeRoomDeletion, deleteRoom, loading: deleteLoading } = useCascadeDelete();
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, roomId: null, roomName: '', analysis: null });
  const [formData, setFormData] = useState({
    name: '',
    capacity: '',
    type: 'CLASSROOM',
    building: '',
    floor: '',
    isAvailable: true
  });
  const [roomStates, setRoomStates] = useState({});
  const [showStateModal, setShowStateModal] = useState(false);
  const [selectedRoomState, setSelectedRoomState] = useState(null);

  useEffect(() => {
    fetchRooms();
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('📨 Rooms.js received WebSocket message:', data);
    
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'room_updated') {
      console.log('🔄 Room update reçu:', data.data?.action);
      fetchRooms();
    } else {
      console.log('❓ Unknown message type in Rooms.js:', data.type);
    }
  }, []);

  // Use WebSocket context
  const { isConnected } = useWebSocket('rooms', handleWebSocketMessage);

  const fetchRooms = async () => {
    try {
      console.log('🔄 Fetching rooms via WebSocket trigger...');
      // Timestamp pour bypasser le cache navigateur (Redis auto-invalidé)
      const response = await axios.get(`${API_URL}/api/rooms?limit=1000&_t=${Date.now()}`);
      const roomsData = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('✅ Rooms fetched:', roomsData.length, 'rooms');
      setRooms(roomsData);
      console.log('📊 Fresh data from backend:', roomsData);
      
      // Appliquer le filtre de recherche existant aux nouvelles données
      if (searchTerm) {
        const filtered = roomsData.filter(room => 
          room.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          room.building?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getRoomTypeLabel(room.type).toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredRooms(filtered);
        console.log('🔍 Applied search filter, showing', filtered.length, 'rooms');
      } else {
        setFilteredRooms(roomsData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des salles:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = rooms.filter(room => 
      room.name.toLowerCase().includes(term) ||
      room.building?.toLowerCase().includes(term) ||
      getRoomTypeLabel(room.type).toLowerCase().includes(term)
    );
    setFilteredRooms(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        capacity: Number(formData.capacity),
        floor: formData.floor ? Number(formData.floor) : undefined
      };
      
      if (editingRoom) {
        await axios.put(`${API_URL}/api/rooms/${editingRoom._id}`, dataToSubmit);
        showSuccess('Salle modifiée avec succès');
      } else {
        await axios.post(`${API_URL}/api/rooms`, dataToSubmit);
        showSuccess('Salle ajoutée avec succès');
      }
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde de la salle');
    }
  };

  const confirmDelete = async (room) => {
    try {
      const analysis = await analyzeRoomDeletion(room._id);
      setDeleteConfirm({ 
        show: true, 
        roomId: room._id, 
        roomName: room.name,
        analysis 
      });
    } catch (error) {
      showError('Erreur lors de l\'analyse de suppression');
    }
  };

  const handleConfirmDelete = async (keepSessions) => {
    if (deleteConfirm.roomId) {
      const shouldKeep = keepSessions === 'keep';
      await deleteRoom(deleteConfirm.roomId, shouldKeep);
      fetchRooms(); // Refresh the list
    }
    setDeleteConfirm({ show: false, roomId: null, roomName: '', analysis: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, roomId: null, roomName: '', analysis: null });
  };

  const openModal = (room = null) => {
    setEditingRoom(room);
    setFormData(room ? {
      name: room.name,
      capacity: room.capacity,
      type: room.type,
      building: room.building || '',
      floor: room.floor || '',
      isAvailable: room.isAvailable
    } : {
      name: '',
      capacity: '',
      type: 'CLASSROOM',
      building: '',
      floor: '',
      isAvailable: true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRoom(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getRoomTypeLabel = (type) => {
    switch (type) {
      case 'CLASSROOM': return 'Salle de cours';
      case 'LAB': return 'Laboratoire';
      case 'AMPHITHEATER': return 'Amphithéâtre';
      default: return type;
    }
  };

  const getRoomTypeIcon = (type) => {
    switch (type) {
      case 'CLASSROOM': return '🏫';
      case 'LAB': return '🔬';
      case 'AMPHITHEATER': return '🎭';
      default: return '🏢';
    }
  };

  const getRoomState = async (roomId, date = new Date(), timeSlot = null) => {
    console.log('getRoomState appelée avec:', { roomId, date, timeSlot });
    try {
      const params = {
        date: date.toISOString().split('T')[0]
      };
      
      if (timeSlot) {
        params.timeSlot = timeSlot;
      }
      
      console.log('URL appelée:', `/api/rooms/${roomId}/state`);
      console.log('Paramètres:', params);
      
      const response = await axios.get(`/api/rooms/${roomId}/state`, { params });
      console.log('Réponse reçue:', response.data);
      alert('Données reçues: ' + JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'état de la salle:', error);
      alert('Erreur API: ' + error.message);
      return { isOccupied: false, currentSession: null, nextSession: null };
    }
  };

  const checkRoomAvailability = async (roomId) => {
    const state = await getRoomState(roomId);
    return {
      isAvailable: !state.isOccupied,
      currentSession: state.currentSession,
      nextSession: state.nextSession
    };
  };

  const handleCheckRoomState = async (roomId) => {
    console.log('handleCheckRoomState appelée avec roomId:', roomId);
    alert('Fonction handleCheckRoomState appelée pour la salle: ' + roomId);
    try {
      console.log('Appel de getRoomState...');
      const state = await getRoomState(roomId);
      console.log('État reçu:', state);
      const room = rooms.find(r => r._id === roomId);
      console.log('Salle trouvée:', room);
      setSelectedRoomState({ ...state, roomName: room?.name });
      setShowStateModal(true);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'état:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const closeStateModal = () => {
    setShowStateModal(false);
    setSelectedRoomState(null);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Salles</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher une salle..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button 
              onClick={() => openModal()} 
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
            >
              + Ajouter
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRooms.map(room => (
            <div key={room._id} className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow ${
              !room.isAvailable ? 'opacity-75' : ''
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getRoomTypeIcon(room.type)}</span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{room.name}</h3>
                    <p className="text-sm text-gray-600">{getRoomTypeLabel(room.type)}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => openModal(room)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => confirmDelete(room)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="w-5">👥</span>
                  <span>{room.capacity} places</span>
                </div>
                {room.building && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="w-5">🏢</span>
                    <span>{room.building}</span>
                  </div>
                )}
                {room.floor !== undefined && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="w-5">🏢</span>
                    <span>Étage {room.floor}</span>
                  </div>
                )}
                <div className="flex items-center text-sm">
                  <span className={`w-2 h-2 rounded-full mr-3 ${
                    room.isAvailable ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  <span className={room.isAvailable ? 'text-green-700' : 'text-red-700'}>
                    {room.isAvailable ? 'Disponible' : 'Indisponible'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredRooms.length === 0 && rooms.length > 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat</h3>
            <p className="text-gray-600">Aucune salle trouvée pour "<strong>{searchTerm}</strong>"</p>
          </div>
        )}
        
        {rooms.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune salle</h3>
            <p className="text-gray-600">Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingRoom ? 'Modifier Salle' : 'Ajouter Salle'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la salle</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="CLASSROOM">Salle de cours</option>
                      <option value="LAB">Laboratoire</option>
                      <option value="AMPHITHEATER">Amphithéâtre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Capacité</label>
                    <input
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      min="1"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bâtiment</label>
                    <input
                      type="text"
                      name="building"
                      value={formData.building}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Ex: Bâtiment A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Étage</label>
                    <input
                      type="number"
                      name="floor"
                      value={formData.floor}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Ex: 1"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isAvailable"
                        checked={formData.isAvailable}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Disponible</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingRoom ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirm.show}
          onClose={cancelDelete}
          onConfirm={handleConfirmDelete}
          title="Supprimer la salle"
          message={`Que faire avec les ${deleteConfirm.analysis?.sessionsAffected || 0} séances utilisant la salle ${deleteConfirm.roomName} ?`}
          showOptions={deleteConfirm.analysis?.sessionsAffected > 0}
          options={[
            { value: 'keep', label: 'Garder les séances sans salle' },
            { value: 'delete', label: 'Supprimer toutes les séances' }
          ]}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default Rooms;